import { FC, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Folder } from "~/api/model";
import { useFtueDemoReport } from "~/features/cases/hooks/useFtueDemoReport";
import { useFtueMediaPreview } from "~/features/cases/hooks/useFtueMediaPreview";
import { useFtueTemplateSectionsView } from "~/features/cases/hooks/useFtueTemplateSectionsView";
import { useTemplateReadyStatus } from "~/features/cases/hooks/useTemplateReadyStatus";
import { useReportSampleUpload } from "~/features/settings/hooks/useReportSampleUpload";
import { ROUTES, useOrganizationParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import { useFtue } from "~/hooks/useFtue";
import {
  FtueCreateCase,
  FtueReportPreview,
  FtueTemplateBuildProgress,
  FtueUploadReport,
} from "./ftue";

enum FtueStep {
  UPLOAD_REPORT = "upload_report",
  WAITING_FOR_TEMPLATE = "waiting_for_template",
  GENERATING_PREVIEW = "generating_preview",
  REPORT_PREVIEW = "report_preview",
  CREATE_CASE = "create_case",
}

interface FtueWelcomeProps {
  demoCase?: Folder;
  isDemoCaseLoading?: boolean;
}

export const FtueWelcome: FC<FtueWelcomeProps> = ({
  demoCase,
  isDemoCaseLoading,
}) => {
  const navigate = useNavigate();
  const intl = useIntl();
  const { organizationId } = useOrganizationParams();
  const { user, completeFtue, isCompletingFtue, trackFtueEvent } = useFtue();
  const { toast } = useToast();
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const [currentStep, setCurrentStep] = useState<FtueStep>(
    FtueStep.UPLOAD_REPORT
  );
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [reportSampleUrl, setReportSampleUrl] = useState<string | null>(null);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [templateFileName, setTemplateFileName] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (documentUrl) URL.revokeObjectURL(documentUrl);
    };
  }, [documentUrl]);

  const preview = useFtueDemoReport(organizationId, templateId);
  const {
    status: previewStatus,
    content: previewContent,
    caseId: previewCaseId,
    reportId: previewReportId,
    generate: generatePreview,
    cancel: cancelPreview,
  } = preview;

  const templateReadyStatus = useTemplateReadyStatus(
    organizationId,
    templateId
  );

  const templateSectionsView = useFtueTemplateSectionsView(
    organizationId,
    templateId,
    previewCaseId,
    previewReportId
  );

  const mediaPreview = useFtueMediaPreview(organizationId, reportSampleUrl);
  const {
    status: mediaPreviewStatus,
    content: mediaPreviewContent,
    generate: generateMediaPreview,
    cancel: cancelMediaPreview,
  } = mediaPreview;

  const mediaPreviewTriggeredRef = useRef(false);

  useEffect(() => {
    if (currentStep !== FtueStep.WAITING_FOR_TEMPLATE) return;

    if (templateReadyStatus === "ready") {
      trackFtueEvent("ftue_template_build_completed");
      setCurrentStep(FtueStep.GENERATING_PREVIEW);
    } else if (
      templateReadyStatus === "failed" ||
      templateReadyStatus === "timeout"
    ) {
      trackFtueEvent("ftue_template_build_failed");
      setTemplateFileName(null);
      toast({
        variant: "destructive",
        title: intl.formatMessage({
          id: "ftue.waitingForTemplate.error",
          defaultMessage: "Failed to process template",
        }),
      });
      cancelMediaPreview();
      cancelPreview();
      mediaPreviewTriggeredRef.current = false;
      setReportSampleUrl(null);
      setCurrentStep(FtueStep.CREATE_CASE);
    }
  }, [
    currentStep,
    templateReadyStatus,
    toast,
    intl,
    trackFtueEvent,
    cancelMediaPreview,
    cancelPreview,
  ]);

  useEffect(() => {
    if (currentStep !== FtueStep.GENERATING_PREVIEW) return;

    if (previewStatus === "idle") {
      generatePreview();
    } else if (previewStatus === "error") {
      toast({
        variant: "destructive",
        title: intl.formatMessage({
          id: "ftue.generatingPreview.error",
          defaultMessage: "Failed to generate report preview",
        }),
      });
      setCurrentStep(FtueStep.CREATE_CASE);
    }
  }, [currentStep, previewStatus, generatePreview, toast, intl]);

  // Kick off media-preview generation as soon as we have the uploaded sample's
  // URL — runs in parallel with template build / cascade animation.
  useEffect(() => {
    if (!reportSampleUrl) return;
    if (currentStep !== FtueStep.WAITING_FOR_TEMPLATE) return;
    if (mediaPreviewTriggeredRef.current) return;
    mediaPreviewTriggeredRef.current = true;
    generateMediaPreview();
  }, [reportSampleUrl, currentStep, generateMediaPreview]);

  // Media-preview drives the REPORT_PREVIEW transition (demo-report cascade is
  // visual only). Guard on the active preview phases so a late-arriving
  // terminal status can't snap the user back from a later step.
  useEffect(() => {
    if (
      currentStep !== FtueStep.WAITING_FOR_TEMPLATE &&
      currentStep !== FtueStep.GENERATING_PREVIEW
    ) {
      return;
    }

    if (mediaPreviewStatus === "ready") {
      trackFtueEvent("ftue_preview_generated");
      cancelPreview();
      setCurrentStep(FtueStep.REPORT_PREVIEW);
      return;
    }

    if (mediaPreviewStatus === "error") {
      toast({
        title: intl.formatMessage({
          id: "ftue.mediaPreview.backgroundToast.title",
          defaultMessage: "Generation in progress",
        }),
        description: intl.formatMessage({
          id: "ftue.mediaPreview.backgroundToast.description",
          defaultMessage:
            "You can view the result in the Reports section shortly.",
        }),
      });
      cancelMediaPreview();
      cancelPreview();
      mediaPreviewTriggeredRef.current = false;
      setReportSampleUrl(null);
      setCurrentStep(FtueStep.CREATE_CASE);
    }
  }, [
    currentStep,
    mediaPreviewStatus,
    cancelMediaPreview,
    cancelPreview,
    trackFtueEvent,
    toast,
    intl,
  ]);

  const { uploadReportSample, isUploading, uploadProgress } =
    useReportSampleUpload({ organizationId, setAsDefault: true });

  useEffect(() => {
    if (!hasTrackedView) {
      trackFtueEvent("ftue_welcome_viewed");
      setHasTrackedView(true);
    }
  }, [hasTrackedView, trackFtueEvent]);

  const firstName = user?.name?.split(" ")[0] || "";

  const handleReportUpload = async (file: File) => {
    trackFtueEvent("ftue_report_upload_started");
    try {
      const result = await uploadReportSample(file);
      trackFtueEvent("ftue_report_upload_success");
      setTemplateId(result.templateId);
      setReportSampleUrl(result.reportSampleUrl);
      setDocumentUrl(URL.createObjectURL(file));
      setTemplateFileName(file.name.replace(/\.[^.]+$/, ""));
      setCurrentStep(FtueStep.WAITING_FOR_TEMPLATE);
    } catch {
      toast({
        variant: "destructive",
        title: intl.formatMessage({
          id: "ftue.uploadReport.error",
          defaultMessage: "Failed to upload report",
        }),
      });
    }
  };

  const handleExploreDemoCase = async () => {
    trackFtueEvent("ftue_explore_demo_clicked");
    await completeFtue("explore_demo");
    if (demoCase) {
      navigate(ROUTES.CASE({ organizationId, caseId: demoCase.id }));
    }
  };

  const handleSkip = async () => {
    trackFtueEvent("ftue_skipped");
    await completeFtue("skip");
  };

  const handleCreateCaseClick = () => {
    trackFtueEvent("ftue_create_case_clicked");
  };

  // Step: Upload Report
  if (currentStep === FtueStep.UPLOAD_REPORT) {
    return (
      <FtueUploadReport
        isUploading={isUploading}
        uploadProgress={uploadProgress}
        onUpload={handleReportUpload}
        onSkip={() => {
          trackFtueEvent("ftue_report_upload_skipped");
          setCurrentStep(FtueStep.CREATE_CASE);
        }}
      />
    );
  }

  // Step: Generating preview — stream sections into the preview as soon as the
  // first one completes; the rest render as placeholders until they catch up.
  if (currentStep === FtueStep.GENERATING_PREVIEW && previewContent) {
    return (
      <FtueReportPreview
        sections={templateSectionsView.sections}
        onContinue={() => {
          trackFtueEvent("ftue_preview_continued");
          cancelPreview();
          setCurrentStep(FtueStep.CREATE_CASE);
        }}
      />
    );
  }

  // Step: Waiting for template + generating preview — cascade animation
  if (
    currentStep === FtueStep.WAITING_FOR_TEMPLATE ||
    currentStep === FtueStep.GENERATING_PREVIEW
  ) {
    return (
      <FtueTemplateBuildProgress
        documentUrl={documentUrl ?? undefined}
        sections={templateSectionsView.sections}
        phase={templateSectionsView.phase}
        totalSections={templateSectionsView.totalSections}
        completedCount={templateSectionsView.completedCount}
        onSkip={() => {
          trackFtueEvent("ftue_preview_skipped");
          cancelMediaPreview();
          cancelPreview();
          mediaPreviewTriggeredRef.current = false;
          setReportSampleUrl(null);
          setCurrentStep(FtueStep.CREATE_CASE);
        }}
      />
    );
  }

  // Step: Report Preview
  if (currentStep === FtueStep.REPORT_PREVIEW) {
    return (
      <FtueReportPreview
        content={mediaPreviewContent ?? ""}
        onContinue={() => {
          trackFtueEvent("ftue_preview_continued");
          setCurrentStep(FtueStep.CREATE_CASE);
        }}
      />
    );
  }

  // Step: Create Case
  if (currentStep === FtueStep.CREATE_CASE) {
    return (
      <FtueCreateCase
        firstName={firstName}
        templateName={templateFileName}
        demoCase={demoCase}
        isDemoCaseLoading={isDemoCaseLoading}
        isCompletingFtue={isCompletingFtue}
        onCreateCaseClick={handleCreateCaseClick}
        onCaseCreated={() => completeFtue("create_case")}
        onExploreDemoCase={handleExploreDemoCase}
        onSkip={handleSkip}
      />
    );
  }
};
